import { NgClass } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faCircleInfo } from '@fortawesome/free-solid-svg-icons';
import { RichToolTipDirective } from 'src/app/directives/rich-tool-tip.directive';
import { Attribute } from 'src/app/models/attribute';
import { AttributeAssignmentRow } from 'src/app/models/attribute-assignment-row';
import { FolderState } from 'src/app/models/folder-state';
import { IAttributeAssignmentLight } from 'src/app/models/interfaces/attribute-assignment-light.interface';
import { AttributeAssignmentDialogComponent } from '../attribute-assignment-dialog/attribute-assignment-dialog.component';
import { AttributeAssignmentDialogResult } from '../attribute-assignment-dialog/models/attribute-assignment-dialog-result';

@Component({
    selector: 'app-attribute-assignment-table',
    templateUrl: './attribute-assignment-table.component.html',
    styleUrls: ['./attribute-assignment-table.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: AttributeAssignmentTableComponent,
            multi: true,
        },
    ],
    imports: [FaIconComponent, MatTooltipModule, MatTableModule, MatSortModule, NgClass, MatIconModule, RichToolTipDirective]
})
export class AttributeAssignmentTableComponent implements ControlValueAccessor {
    public attributesAssignmentTooltipContent =
        'Ce paramètre permet de specifier les attributs à renseigner à la réception d’un mail. L’attribut peut prendre une valeur fixe spécifiée par la propriété <b>Valeur</b> ou une valeur dynamique dans le cas d’une expression régulière (voir l’aide contextuelle du champ <b>Expression de recherche</b>).<br>Dans le cas d’un attribut de type <b>Date</b> ou <b>Date&Heure</b> il est possible d’indiquer le format utilisé pour lire la date dans le mail reçu à l’aide de la propriété <b>Format</b>.';

    public faCircleInfo = faCircleInfo;

    /** Liste des colonnes du tableau listant les propriétés. */
    attributesTableColumns: string[] = ['name', 'value', 'format', 'action'];

    /** Liste des assignations d'attributs. */
    private _attributesAssignment: AttributeAssignmentRow[];

    disabled: boolean = false;

    /** Setter permettant de spécifier les assignations d'attributs et de mettre à jour le tableau.*/
    @Input() set attributesAssignment(value: AttributeAssignmentRow[]) {
        this._attributesAssignment = value;
        this.attributesDatasource = new MatTableDataSource(this.attributesAssignment);
    }

    /** Getter permettant de récupérer les assignations d'attributs */
    get attributesAssignment(): AttributeAssignmentRow[] {
        return this._attributesAssignment;
    }

    /** Emis lorsque la liste des assignations d'attributs est modifiée. */
    @Output() attributesChanged = new EventEmitter<AttributeAssignmentRow[]>();

    /** Datasource du tableau listant les assignations d'attributs. */
    attributesDatasource: MatTableDataSource<AttributeAssignmentRow>;

    onChange: (value?: IAttributeAssignmentLight[]) => void;

    onTouch: () => void;

    _attributes: Attribute[] = [];

    @Input()
    public set attributes(value: Attribute[]) {
        this._attributes = value;
    }

    _folderStates: FolderState[] = [];

    @Input()
    public set folderStates(value: FolderState[]) {
        this._folderStates = value;
    }

    constructor(public dialog: MatDialog) {
        // Initialise la datasource du tableau listant les propriétés.
        this.attributesDatasource = new MatTableDataSource(this.attributesAssignment);
    }

    // upon UI element value changes, this method gets triggered
    registerOnChange(fn: any) {
        this.onChange = fn;
    }

    // upon touching the element, this method gets triggered
    registerOnTouched(fn: any) {
        this.onTouch = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;

        if (isDisabled) {
            const index = this.attributesTableColumns.findIndex((x) => x === 'action');

            if (index >= 0) {
                this.attributesTableColumns.splice(index, 1);
            }
        } else {
            const index = this.attributesTableColumns.findIndex((x) => x === 'action');

            if (index < 0) {
                this.attributesTableColumns.push('action');
            }
        }
    }

    // this method sets the value programmatically
    writeValue(attributeAssignments: IAttributeAssignmentLight[]) {
        console.log('value', attributeAssignments);

        let tab = [];

        if (attributeAssignments) {
            attributeAssignments.forEach((attributeAssignment) => {
                let attributeId = attributeAssignment.attributeId;
                let attributeValue = attributeAssignment.attributeValue;
                let attributeFormat = attributeAssignment.attributeFormat;
                let attributeName = this._attributes.find((x) => x.id == attributeId).header;
                tab.push({
                    attributeId: attributeId,
                    attributeName: attributeName,
                    attributeValue: attributeValue,
                    attributeFormat: attributeFormat,
                });
            });
        }

        this.attributesAssignment = tab;
    }

    /**
     * Emis lorsque l'utilisateur supprime une assignation d'attribut.
     * @param attributeId: Id de l'attribut à supprimer.
     */
    public onDeleteAttribute(attributeId: number) {
        const index = this.attributesAssignment.findIndex((x) => x.attributeId === attributeId);
        this.attributesAssignment.splice(index, 1);
        this.attributesDatasource = new MatTableDataSource(this.attributesAssignment);
        this.applyChange();
    }

    /**
     * Emis lorsque l'utilisateur edite une assignation d'attribut.
     * @param attributeId: Id de l'attribut à éditer.
     */
    public onEditAttribute(attributeAssignmentRow: AttributeAssignmentRow) {
        console.log("Édition de l'attribut", attributeAssignmentRow);

        const dialogRef = this.dialog.open(AttributeAssignmentDialogComponent, {
            maxWidth: '400px',
            data: {
                title: "Édition de l'attribut",
                row: attributeAssignmentRow,
                attributes: this._attributes,
                folderStates: this._folderStates,
            },
            panelClass: 'empty-dialog',
        });

        // Attente de la réponse de la boite de dialogue de saisie.
        dialogRef.afterClosed().subscribe((dialogResult: AttributeAssignmentDialogResult) => {
            if (dialogResult.confirmation) {
                // Mise à jour de l'assignation d'attribut.
                const index = this.attributesAssignment.findIndex((x) => x.attributeId === dialogResult.value.attributeId);
                this.attributesAssignment.splice(index, 1, dialogResult.value);
                this.attributesDatasource = new MatTableDataSource(this.attributesAssignment);
                this.applyChange();
            }
        });
    }

    /**
     * Emis lorsque l'utilisateur ajoute une assignation d'attribut.
     */
    public onAddAttribute() {
        console.log("Ajout d'un attribut");

        const dialogRef = this.dialog.open(AttributeAssignmentDialogComponent, {
            maxWidth: '400px',
            data: {
                title: "Ajout d'un attribut",
                row: null as AttributeAssignmentRow,
                attributes: this._attributes,
                folderStates: this._folderStates,
            },
            panelClass: 'empty-dialog',
        });

        // Attente de la réponse de la boite de dialogue de saisie
        dialogRef.afterClosed().subscribe((dialogResult: AttributeAssignmentDialogResult) => {
            if (dialogResult.confirmation) {
                // Ajout de l'assignation d'attribut.
                this.attributesAssignment.push(dialogResult.value);
                this.attributesDatasource = new MatTableDataSource(this.attributesAssignment);
                this.applyChange();
            }
        });
    }

    private applyChange(): void {
        this.attributesChanged.emit(this.attributesAssignment);

        if (this.onChange) {
            let tab: IAttributeAssignmentLight[] = [];

            this.attributesAssignment.forEach((x) => tab.push({ attributeId: x.attributeId, attributeValue: x.attributeValue, attributeFormat: x.attributeFormat }));
            this.onChange(tab);
        }
    }
}
